import React, { useState } from 'react';
import { 
  ChartBarIcon,
  DocumentArrowDownIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface RevenueProjection {
  pond_id: string;
  pond_name: string;
  current_biomass_kg: number;
  estimated_harvest_kg: number;
  current_market_price_per_kg: number;
  projected_revenue: number;
  estimated_harvest_date?: string;
  confidence_level: string;
}

const RevenueProjections: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [marketPrice, setMarketPrice] = useState<number>(200);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [projections, setProjections] = useState<RevenueProjection[]>([]);

  const fetchProjections = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/reports/revenue-projections', {
        params: {
          location_id: selectedLocation.id,
          market_price_per_kg: marketPrice,
        },
      });
      setProjections(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load revenue projections');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = async () => {
    if (!selectedLocation) return;
    
    try {
      const response = await apiClient.get('/reports/export/revenue-projections', {
        params: {
          location_id: selectedLocation.id,
        },
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue_projections_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getConfidenceColor = (level: string) => {
    switch (level) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getConfidenceIcon = (level: string) => {
    switch (level) {
      case 'high': return <CheckCircleIcon className="h-4 w-4" />;
      case 'medium': return <ChartBarIcon className="h-4 w-4" />;
      case 'low': return <ExclamationTriangleIcon className="h-4 w-4" />;
      default: return <ChartBarIcon className="h-4 w-4" />;
    }
  };

  const totalProjectedRevenue = projections.reduce((sum, p) => sum + p.projected_revenue, 0);
  const totalCurrentBiomass = projections.reduce((sum, p) => sum + p.current_biomass_kg, 0);
  const totalEstimatedHarvest = projections.reduce((sum, p) => sum + p.estimated_harvest_kg, 0);

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <ArrowTrendingUpIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Revenue Projections</h1>
        <p className="text-lg text-gray-600">
          Projected revenue based on current biomass and market prices
        </p>
      </div>

      {/* Market Price Input */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Market Price per Kg (INR)
            </label>
            <input
              type="number"
              value={marketPrice}
              onChange={(e) => setMarketPrice(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              step="0.01"
              min="0"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchProjections}
              disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <ChartBarIcon className="h-4 w-4" />
              {loading ? 'Loading...' : 'Generate Projections'}
            </button>
            {projections.length > 0 && (
              <button
                onClick={exportToCSV}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Summary Cards */}
      {projections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(totalProjectedRevenue)}
            </div>
            <div className="text-sm text-gray-600">Total Projected Revenue</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {totalCurrentBiomass.toFixed(1)} kg
            </div>
            <div className="text-sm text-gray-600">Current Biomass</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {totalEstimatedHarvest.toFixed(1)} kg
            </div>
            <div className="text-sm text-gray-600">Estimated Harvest</div>
          </div>
        </div>
      )}

      {/* Projections Table */}
      {projections.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Pond Projections</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pond
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Biomass
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estimated Harvest
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Market Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projected Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projections.map((projection, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {projection.pond_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {projection.current_biomass_kg.toFixed(1)} kg
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {projection.estimated_harvest_kg.toFixed(1)} kg
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(projection.current_market_price_per_kg)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                      {formatCurrency(projection.projected_revenue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(projection.confidence_level)}`}>
                        {getConfidenceIcon(projection.confidence_level)}
                        {projection.confidence_level.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {projections.length === 0 && !loading && (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Projections Available</h3>
          <p className="text-gray-600">
            Generate projections to see revenue estimates for your ponds.
          </p>
        </div>
      )}
    </div>
  );
};

export default RevenueProjections;
